import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { Download, X, Bike, Car, Share, ArrowDown } from "lucide-react";
import { toast } from "sonner";
import { isNoldDomain } from "../lib/domain";

function isStandalone() {
  return window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
}
function isMobile() {
  return /iphone|ipad|ipod|android/i.test(window.navigator.userAgent) || window.innerWidth < 768;
}
function isIOS() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent);
}

export function InstallBanner() {
  const { t } = useTranslation();
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const ios = isIOS();

  useEffect(() => {
    if (isStandalone() || localStorage.getItem("bsg_install_dismissed") || !isMobile()) return;
    const handler = (e) => { e.preventDefault(); setDeferred(e); };
    window.addEventListener("beforeinstallprompt", handler);
    const timer = setTimeout(() => setShow(true), 4000);
    return () => { window.removeEventListener("beforeinstallprompt", handler); clearTimeout(timer); };
  }, []);

  const dismiss = () => { localStorage.setItem("bsg_install_dismissed", "1"); setShow(false); };
  const install = async () => {
    if (deferred) {
      try {
        deferred.prompt();
        await deferred.userChoice;
      } catch { /* noop */ }
      dismiss();
      return;
    }
    // Fallback : aucun événement PWA disponible (Chrome n'a pas encore validé les critères d'engagement,
    // navigateur non compatible, ou déjà installé sous une autre forme). On guide l'utilisateur manuellement.
    if (isIOS()) {
      toast.info("Appuyez sur Partager (en bas de Safari), puis « Sur l'écran d'accueil ».", { duration: 8000 });
    } else {
      toast.info("Ouvrez le menu ⋮ de votre navigateur et choisissez « Installer l'application » ou « Ajouter à l'écran d'accueil ».", { duration: 8000 });
    }
  };

  if (!show) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 140, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 140, opacity: 0 }}
        transition={{ type: "spring", damping: 24, stiffness: 280 }}
        className="fixed bottom-4 inset-x-4 z-[1100] max-w-md mx-auto"
        data-testid="install-banner"
      >
        <div className="relative rounded-3xl bg-white shadow-[0_20px_60px_rgba(0,0,0,0.25)] border border-[#F0E6DA] p-5 overflow-hidden">
          <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-[#F26419]/25 blur-2xl" />
          <button data-testid="install-banner-close" onClick={dismiss} className="absolute top-3 right-3 w-8 h-8 rounded-lg hover:bg-[#F0EBE1] flex items-center justify-center z-10">
            <X className="w-4 h-4 text-[#75807A]" />
          </button>

          <div className="relative flex items-center gap-3">
            <span className="w-14 h-14 shrink-0 rounded-2xl bg-gradient-to-br from-[#F26419] to-[#D94F2B] flex items-center justify-center shadow-lg">
              {isNoldDomain() ? <Car className="w-7 h-7 text-white" /> : <Bike className="w-7 h-7 text-white" />}
            </span>
            <div className="min-w-0 pr-6">
              <div className="font-head font-black text-lg text-[#1A1A1A] leading-tight">{isNoldDomain() ? t("install.bannerTitle").replace(/BentSkin\s*Go/gi, "Nold Go").replace(/install\s+bentskin\s*go/gi, "Install Nold Go").replace(/instalar\s+bentskin\s*go/gi, "Instalar Nold Go").replace(/bentskin\s*go\s+installieren/gi, "Nold Go installieren") : t("install.bannerTitle")}</div>
              <div className="text-sm text-[#75807A] mt-0.5">{ios ? t("install.iosShareHint") : t("install.bannerText")}</div>
            </div>
          </div>

          {ios && !deferred ? (
            <div className="relative mt-4 flex flex-col items-center" data-testid="install-banner-ios">
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#1A1A1A] text-white text-sm font-semibold">
                <Share className="w-4 h-4 text-[#F26419]" /> {t("install.iosShareWord")}
              </span>
              <motion.div
                animate={{ y: [0, 10, 0] }}
                transition={{ repeat: Infinity, duration: 1.1, ease: "easeInOut" }}
                className="mt-1"
              >
                <ArrowDown className="w-8 h-8 text-[#D94F2B]" strokeWidth={3} />
              </motion.div>
            </div>
          ) : (
            <button
              data-testid="install-banner-cta"
              onClick={install}
              className="mt-4 w-full h-14 rounded-2xl bg-[#F26419] hover:bg-[#D85312] active:scale-[0.98] text-[#1A1A1A] font-black text-base flex items-center justify-center gap-2 transition-transform shadow-lg shadow-[#F26419]/40"
            >
              <Download className="w-5 h-5" /> {t("install.installApp")}
            </button>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
